/**
 * Sound levels (1.7.4, docs/dev/editor-1.7.4-build.md): volume keys on a clip, crossfades at cuts, track gain. Pure;
 * the editor's preview, the compose filter graph, the NLE writers and the ops all read levels through these, so the
 * preview and the export agree.
 *
 * Levels are in dB. A clip's level at source time t is `gainDb + curve(t)`, where curve is its volume keys, linear in
 * dB between keys and held flat before the first and after the last (0 with no keys). The level is clamped to
 * MIN_DB…MAX_DB; at MIN_DB (−60) or below it is silence (dbToGain gives 0). A sound track's gainDb (trackAudio) is
 * added on top, then mute and solo apply as before; fades and crossfades multiply the result.
 */
import { TICKS_PER_SECOND, ticksPerFrame, type Rational } from './time.ts';
import {
  A1_TRACK, MAX_DB, MIN_DB, allAudioTracks, allClips, audioTrackOf, clipDuration, layoutVideo,
  type AudioClip, type CrossfadeAlign, type SequenceDocument, type SoundTrack, type VideoClip, type VolumeKey,
} from './sequence.ts';
import type { ItemRef } from './sequence-ops.ts';

/** The longest crossfade (seconds); the handles and the outgoing clip's length usually limit it first. */
export const MAX_CROSSFADE_SECONDS = 10;

const clampDb = (db: number) => Math.min(MAX_DB, Math.max(MIN_DB, db));

/** The keys' curve (dB) at source time t: linear between keys, flat before the first and after the last; 0 with none. */
export function keyCurveDb(keys: readonly VolumeKey[] | undefined, sourceTicks: number): number {
  if (!keys?.length) return 0;
  const first = keys[0]!;
  const last = keys.at(-1)!;
  if (sourceTicks <= first.atTicks) return first.db;
  if (sourceTicks >= last.atTicks) return last.db;
  for (let i = 1; i < keys.length; i++) {
    const b = keys[i]!;
    if (sourceTicks <= b.atTicks) {
      const a = keys[i - 1]!;
      return a.db + ((b.db - a.db) * (sourceTicks - a.atTicks)) / (b.atTicks - a.atTicks);
    }
  }
  return last.db;
}

/** A clip's level (dB) at source time `sourceTicks`: gainDb plus its keys' curve, clamped to MIN_DB…MAX_DB. Mute is not applied. */
export function volumeAt(clip: { gainDb: number; volumeKeys?: readonly VolumeKey[] }, sourceTicks: number): number {
  return clampDb(clip.gainDb + keyCurveDb(clip.volumeKeys, sourceTicks));
}

/** A level as a linear gain factor: 10^(dB/20), and 0 (silence) at MIN_DB or below. */
export function dbToGain(db: number): number {
  return db <= MIN_DB ? 0 : Math.pow(10, db / 20);
}

/** One point of a clip's level curve: source time and the level there (dB, gainDb included and clamped). */
export interface LevelPoint { atTicks: number; db: number }

/**
 * A clip's whole level curve over its source range, as points to join with straight lines (in dB): the first at
 * sourceInTicks, the last at sourceOutTicks (the same point twice for an empty clip), every key inside, and where the
 * unclamped level crosses MIN_DB or MAX_DB a point there, so straight lines between the points are exactly volumeAt.
 * Without keys: two points at gainDb. What compose turns into a piecewise-linear `volume` expression in `t` (seconds
 * from the clip's start: (atTicks − sourceInTicks) / TICKS_PER_SECOND) and the preview schedules on a GainNode.
 */
export function levelPoints(clip: { gainDb: number; volumeKeys?: readonly VolumeKey[]; sourceInTicks: number; sourceOutTicks: number }): LevelPoint[] {
  const s = clip.sourceInTicks;
  const e = Math.max(s, clip.sourceOutTicks);
  const raw = (t: number) => clip.gainDb + keyCurveDb(clip.volumeKeys, t);
  const times = [s, ...(clip.volumeKeys ?? []).map((k) => k.atTicks).filter((t) => t > s && t < e), e];
  const out: LevelPoint[] = [];
  for (let i = 0; i < times.length; i++) {
    const t = times[i]!;
    if (i > 0) {
      const t0 = times[i - 1]!;
      const a = raw(t0), b = raw(t);
      for (const bound of [MIN_DB, MAX_DB]) {
        if ((a - bound) * (b - bound) < 0) out.push({ atTicks: Math.round(t0 + ((bound - a) / (b - a)) * (t - t0)), db: bound });
      }
    }
    out.push({ atTicks: t, db: clampDb(raw(t)) });
  }
  return out.sort((x, y) => x.atTicks - y.atTicks);
}

/** The equal-power crossfade curve (ffmpeg afade curve=qsin): the incoming gain at progress p (0…1); the outgoing is equalPower(1 − p). */
export function equalPower(p: number): number {
  return Math.sin((Math.min(1, Math.max(0, p)) * Math.PI) / 2);
}

/** A sound track's gain (dB, trackAudio[track].gainDb; 0 when not set). */
export function trackGainDb(doc: SequenceDocument, track: SoundTrack): number {
  return doc.trackAudio?.[track]?.gainDb ?? 0;
}

/**
 * The length of each source (1.8.0), by version id, for the tail handles (media after a clip's Out): ticks (for a
 * recording, what is recorded so far), Infinity for a still (it has no end). A version left out is of unknown length.
 * The editor builds it from /sources, compose from the probes (sourceDurationsOf), so the preview and the export agree.
 */
export type SourceDurations = ReadonlyMap<string, number>;

/** A clip's crossfade alignment (1.8.0): crossfadeAlign, or 'end' (at the cut, as in 1.7.4) when left out. */
export const crossfadeAlignOf = (c: { crossfadeAlign?: 'center' | 'start' }): CrossfadeAlign => c.crossfadeAlign ?? 'end';

/** The alignments' names, as the context menu and the Inspector show them (1.8.0). */
export const CROSSFADE_ALIGN_LABELS: Record<CrossfadeAlign, string> = { center: 'Centre on cut', start: 'Start at cut', end: 'End at cut' };

const wholeFramesDown = (ticks: number, tpf: number) => Math.max(0, Math.floor(ticks / tpf + 1e-9) * tpf);

/**
 * A crossfade of `ticks` split at its cut (1.8.0): `pre` before the cut (the incoming sound pre-rolled from its head
 * handle), `post` after it (the outgoing sound carried on from its tail handle). End: all before; start: all after;
 * centre: half each in whole frames, an odd frame after the cut.
 */
export function crossfadeSplit(ticks: number, align: CrossfadeAlign, rate: Rational): { pre: number; post: number } {
  if (align === 'end') return { pre: ticks, post: 0 };
  if (align === 'start') return { pre: 0, post: ticks };
  const pre = wholeFramesDown(ticks / 2, ticksPerFrame(rate));
  return { pre, post: ticks - pre };
}

/**
 * How much media a clip has after its Out (1.8.0): the source's length (`durations`, by version id; for a multicam clip
 * at most its angle's) minus its Out, never below 0. Infinity for a still, and when the length is not known (no map, or
 * the version not in it), so a crossfade is never cut short for want of a length; the ops ask for one first.
 */
export function tailHandle(doc: SequenceDocument, c: { versionId: string; sourceOutTicks: number; multicam?: VideoClip['multicam'] }, durations?: SourceDurations): number {
  let end = durations?.get(c.versionId) ?? Infinity;
  if (!(end > 0)) end = Infinity;
  const g = c.multicam && (doc.multicam ?? []).find((x) => x.id === c.multicam!.groupId);
  const a = g?.angles[c.multicam!.angle];
  if (a) end = Math.min(end, a.durationTicks);
  return Math.max(0, end - c.sourceOutTicks);
}

/** The cut before a clip that a crossfade would blend: the two clips, the track and the cut's record time. */
export interface CrossfadeCut {
  incoming: VideoClip | AudioClip;
  outgoing: VideoClip | AudioClip;
  /** 'a1' for a V1 cut, else the audio track ('a2' or an audioTracks id). */
  track: SoundTrack;
  /** Where the incoming clip starts (= where the outgoing one ends), record ticks. */
  cutTicks: number;
}

/** What a crossfade into a clip can be: the longest it may be (whole frames, 0 when none), when none why, and (1.8.0) for which alignment. */
export interface CrossfadeLimits { cut: CrossfadeCut | null; maxTicks: number; problem: string | null; align: CrossfadeAlign }

/** What crossfadeLimits takes (1.8.0): the alignment (default the clip's own), the source lengths, the rate. */
export interface CrossfadeOptions { align?: CrossfadeAlign; durations?: SourceDurations; rate?: Rational }

/**
 * The crossfade limits for the cut before `ref` (the incoming clip):
 *  - an 'audio' clip: the clip on the same audio track that ends exactly where it starts;
 *  - a 'video' clip (A1): the V1 clip before it, with no gap and no transition between them (a transition blends the
 *    sound already) and both clips' sound on A1 (neither soundDetached).
 * maxTicks is in whole frames and at most MAX_CROSSFADE_SECONDS; by alignment (1.8.0; `options.align`, default the
 * clip's own):
 *  - end (1.7.4): within the incoming clip's head handle (its sourceInTicks: media before its In) and the outgoing clip;
 *  - start: within the outgoing clip's tail handle (tailHandle, with `options.durations`) and the incoming clip;
 *  - centre: the part before the cut (crossfadeSplit) within the head handle and the outgoing clip, the part after it
 *    within the tail handle and the incoming clip.
 * A cut with no room has a problem saying why. The third argument may be the rate alone, as in 1.7.4.
 */
export function crossfadeLimits(doc: SequenceDocument, ref: ItemRef, options: CrossfadeOptions | Rational = {}): CrossfadeLimits {
  const opts: CrossfadeOptions = 'num' in options ? { rate: options } : options;
  const tpf = ticksPerFrame(opts.rate ?? doc.frameRate);
  const own = clipOf(doc, ref);
  const align = opts.align ?? (own ? crossfadeAlignOf(own) : 'end');
  const none = (problem: string, cut: CrossfadeCut | null = null): CrossfadeLimits => ({ cut, maxTicks: 0, problem, align });
  let cut: CrossfadeCut | null = null;
  if (ref.kind === 'video') {
    const placed = layoutVideo(doc);
    const i = placed.findIndex((p) => p.clip.id === ref.id);
    if (i < 0) return none('Pick a clip on V1 or an audio track');
    const p = placed[i]!, prev = placed[i - 1];
    if (prev && p.transition) return none('The transition already blends the sound at this cut');
    if (!prev || p.gapBefore > 0 || prev.recordOut !== p.recordIn) return none('Put the clip right after another one to crossfade into it');
    if (p.clip.soundDetached || prev.clip.soundDetached) return none('The sound is unlinked from the picture: crossfade it on its audio track');
    cut = { incoming: p.clip, outgoing: prev.clip, track: A1_TRACK, cutTicks: p.recordIn };
  } else if (ref.kind === 'audio') {
    const trackId = audioTrackOf(doc, ref.id);
    const clips = allAudioTracks(doc).find((t) => t.id === trackId)?.clips ?? [];
    const c = clips.find((x) => x.id === ref.id);
    if (!c || !trackId) return none('Pick a clip on V1 or an audio track');
    const prev = clips.find((x) => x.id !== c.id && clipDuration(x) > 0 && x.recordInTicks + clipDuration(x) === c.recordInTicks);
    if (!prev) return none('Put the clip right after another one to crossfade into it');
    cut = { incoming: c, outgoing: prev, track: trackId, cutTicks: c.recordInTicks };
  } else return none('Pick a clip on V1 or an audio track');
  const most = MAX_CROSSFADE_SECONDS * TICKS_PER_SECOND;
  const head = cut.incoming.sourceInTicks;
  const tail = align === 'end' ? 0 : tailHandle(doc, cut.outgoing, opts.durations);
  let max: number;
  if (align === 'end') max = wholeFramesDown(Math.min(head, clipDuration(cut.outgoing), most), tpf);
  else if (align === 'start') max = wholeFramesDown(Math.min(tail, clipDuration(cut.incoming), most), tpf);
  else {
    // In frames: the part before the cut (half, rounded down) within H, the part after it (the rest) within T.
    const H = Math.floor(Math.min(head, clipDuration(cut.outgoing)) / tpf + 1e-9);
    const T = Math.floor(Math.min(tail, clipDuration(cut.incoming)) / tpf + 1e-9);
    const frames = Math.max(2 * Math.min(H, T), T >= 1 ? 2 * Math.min(H, T - 1) + 1 : 0);
    max = Math.min(frames * tpf, wholeFramesDown(most, tpf));
  }
  if (max <= 0) {
    if (align !== 'start' && head < tpf) return none('The clip has no media before its In to crossfade with', cut);
    if (align !== 'end' && tail < tpf) return none('The clip before has no media after its Out to crossfade with', cut);
    return none(align === 'end' ? 'The clip before is too short to crossfade' : 'The clips are too short to crossfade', cut);
  }
  return { cut, maxTicks: max, problem: null, align };
}

/**
 * The crossfade that applies into a clip (ticks, whole frames): its crossfadeInTicks clamped to crossfadeLimits for its
 * own alignment, 0 when it has none or the cut no longer qualifies (a dormant crossfadeInTicks, e.g. after a clip was
 * moved). What the preview, compose, the NLE writers and composeRequirements use; never read crossfadeInTicks directly.
 * 1.8.0: `durations` limits a centred or start-aligned crossfade by the tail handle (without them the tail is open).
 */
export function appliedCrossfade(doc: SequenceDocument, ref: ItemRef, durations?: SourceDurations): number {
  const want = clipOf(doc, ref)?.crossfadeInTicks ?? 0;
  if (want <= 0) return 0;
  const lim = crossfadeLimits(doc, ref, { durations });
  return lim.problem ? 0 : Math.min(wholeFramesDown(want, ticksPerFrame(doc.frameRate)), lim.maxTicks);
}

/** A crossfade as it applies (1.8.0): the cut, its length and alignment, its parts before and after the cut, and the blend [fromTicks, toTicks). */
export interface CrossfadeSpan { cut: CrossfadeCut; ticks: number; align: CrossfadeAlign; pre: number; post: number; fromTicks: number; toTicks: number }

/** The crossfade into `ref` as it applies (appliedCrossfade) and where it sits; null when none applies. */
export function crossfadeSpan(doc: SequenceDocument, ref: ItemRef, durations?: SourceDurations): CrossfadeSpan | null {
  const ticks = appliedCrossfade(doc, ref, durations);
  if (ticks <= 0) return null;
  const lim = crossfadeLimits(doc, ref, { durations });
  if (!lim.cut) return null;
  const { pre, post } = crossfadeSplit(ticks, lim.align, doc.frameRate);
  return { cut: lim.cut, ticks, align: lim.align, pre, post, fromTicks: lim.cut.cutTicks - pre, toTicks: lim.cut.cutTicks + post };
}

/**
 * The alignment a new crossfade of `ticks` into `ref` gets (1.8.0, ops.setCrossfade without one): `prefer` (centre)
 * when it is as long there as anywhere, else the one where it is longest (End at cut with no tail handle, Start at cut
 * with no head handle). Null when none fits.
 */
export function bestCrossfadeAlign(doc: SequenceDocument, ref: ItemRef, ticks: number, durations?: SourceDurations, prefer: CrossfadeAlign = 'center'): CrossfadeAlign | null {
  const order: CrossfadeAlign[] = [prefer, ...(['center', 'end', 'start'] as const).filter((a) => a !== prefer)];
  let best: CrossfadeAlign | null = null;
  let len = 0;
  for (const a of order) {
    const lim = crossfadeLimits(doc, ref, { align: a, durations });
    const got = lim.problem ? 0 : Math.min(ticks, lim.maxTicks);
    if (got > len) { best = a; len = got; }
  }
  return best;
}

/** The source lengths of a document's clips from their probes (1.8.0, compose: SourceDurations by version id; a still is Infinity). */
export function sourceDurationsOf(doc: SequenceDocument, probeOf: (clipId: string) => { mediaKind: string; durationTicks: number } | undefined): SourceDurations {
  const out = new Map<string, number>();
  for (const { clip } of allClips(doc)) {
    const p = probeOf(clip.id);
    if (p && !out.has(clip.versionId)) out.set(clip.versionId, p.mediaKind === 'image' ? Infinity : p.durationTicks);
  }
  return out;
}

function clipOf(doc: SequenceDocument, ref: ItemRef): VideoClip | AudioClip | undefined {
  if (ref.kind === 'video') return doc.video.find((c) => c.id === ref.id);
  if (ref.kind === 'audio') return allAudioTracks(doc).flatMap((t) => t.clips).find((c) => c.id === ref.id);
  return undefined;
}
